// ============================================================================
// apiBase - single source of truth for the Cloud Functions API URL
// ============================================================================
// Resolution order (first match wins):
//   1. REACT_APP_USE_EMULATOR=true env flag → always emulator (explicit opt-in).
//   2. REACT_APP_USE_EMULATOR=false env flag → always prod (explicit opt-out).
//   3. Auto-detect: window.location.hostname is localhost/127.0.0.1 →
//      emulator. This is the "it just works" default for `npm start`.
//   4. REACT_APP_API_URL / REACT_APP_FUNCTIONS_URL env vars (custom override).
//   5. PROD_API_BASE (production fallback).
//
// Hardcoding prod URLs in service files leaks calls to production while in
// dev mode - DO NOT do that.
// ============================================================================

const PROD_API_BASE = 'https://us-central1-pokersweepstars.cloudfunctions.net/api';
const EMULATOR_API_BASE = 'http://localhost:5001/pokersweepstars/us-central1/api';

function isLocalHostname(): boolean {
  if (typeof window === 'undefined' || !window.location) return false;
  // Capacitor native shells serve the bundle from localhost (capacitor://
  // on iOS, http://localhost on Android) - that is NOT a dev environment.
  // Without this guard a production iOS build would auto-route to a
  // nonexistent local emulator.
  const proto = window.location.protocol;
  if (proto === 'capacitor:' || proto === 'ionic:') return false;
  if ((window as any).Capacitor?.isNativePlatform?.()) return false;
  const host = window.location.hostname;
  return host === 'localhost'
    || host === '127.0.0.1'
    || host === '0.0.0.0'
    || host.endsWith('.local');
}

export function getApiBase(): string {
  // Explicit opt-in to emulator.
  if (process.env.REACT_APP_USE_EMULATOR === 'true') return EMULATOR_API_BASE;
  // Explicit opt-out - useful when testing prod from a localhost dev build.
  if (process.env.REACT_APP_USE_EMULATOR === 'false') {
    return process.env.REACT_APP_API_URL
      || process.env.REACT_APP_FUNCTIONS_URL
      || PROD_API_BASE;
  }
  // Auto-detect: served from localhost → assume the emulator is running on
  // the standard port. This is the default for `npm start` and means a
  // fresh clone "just works" without anyone editing .env.local.
  if (isLocalHostname()) return EMULATOR_API_BASE;
  // Custom remote API or production fallback.
  return process.env.REACT_APP_API_URL
    || process.env.REACT_APP_FUNCTIONS_URL
    || PROD_API_BASE;
}

/**
 * True when this client session is talking to the local emulator - either
 * via the explicit env flag or the localhost auto-detect above. Use this
 * everywhere emulator-only UI is gated (admin bootstrap button, test
 * tooling) so the gate can never drift from where API calls actually go.
 */
export function isEmulatorEnv(): boolean {
  if (process.env.REACT_APP_USE_EMULATOR === 'true') return true;
  if (process.env.REACT_APP_USE_EMULATOR === 'false') return false;
  return isLocalHostname();
}

/** Base URL constant - safe to use at module init since env vars are
 *  bundled at build time by CRA. The hostname check runs once on load. */
export const API_BASE = getApiBase();

/**
 * URL of a STANDALONE (non-Express) Cloud Function such as requestPayout or
 * executeRedemptionPayout. Production uses the 2nd-gen Cloud Run host; the
 * emulator serves them next to the api function. Hardcoding the production
 * host in a page silently bypasses the emulator (and its auth), which is how
 * "Invalid auth token" shows up during local testing.
 */
export function functionUrl(name: string): string {
  if (isEmulatorEnv()) return `http://localhost:5001/pokersweepstars/us-central1/${name}`;
  return `https://${name.toLowerCase()}-efcly6byiq-uc.a.run.app`;
}
